import { useState } from 'react';

import PropTypes from 'prop-types';
import { BsSlashCircle } from 'react-icons/bs';

import TextInput from '#globalComponents/Forms/TextInput';
import ConfirmModal from '#globalComponents/Overlay/ConfirmModal';
import { useErrorStore } from '#lib/stores/errorStore';

import { useCreateBlacklistEntries } from '../../models/mutations';

const formatCount = (count) => count.toLocaleString('de-DE');

const COPY = {
	email: {
		title: (count) =>
			count === 1
				? 'Kontakt auf die Blacklist setzen'
				: `${formatCount(count)} Kontakte auf die Blacklist setzen`,
		message:
			'Die E-Mail-Adressen werden auf die Blacklist gesetzt und von E-Mail- und Post-Kampagnen ausgeschlossen.',
		unit: 'E-Mail-Adressen',
		skipHint: 'ohne gültige E-Mail-Adresse',
	},
	domain: {
		title: (count) =>
			count === 1 ? 'Firma auf die Blacklist setzen' : `${formatCount(count)} Firmen auf die Blacklist setzen`,
		message:
			'Die Domains der Firmen (Website und E-Mail) werden auf die Blacklist gesetzt – damit sind alle E-Mail-Adressen dieser Domains von E-Mail- und Post-Kampagnen ausgeschlossen.',
		unit: 'Domains',
		skipHint: 'ohne Domain oder Freemail-Domain',
	},
};

const describeResult = ({ added, alreadyBlocked, skipped }, { unit, skipHint }) =>
	[
		`${formatCount(added)} ${unit} auf die Blacklist gesetzt`,
		alreadyBlocked > 0 && `${formatCount(alreadyBlocked)} bereits auf der Blacklist`,
		skipped > 0 && `${formatCount(skipped)} übersprungen (${skipHint})`,
	]
		.filter(Boolean)
		.join(', ');

/**
 * Confirms putting the selected list recipients on the org blacklist. Recipients stay in the list;
 * the send paths skip blacklisted addresses.
 */
const BlacklistSelectionModal = ({ entryType, entries, selectedCount, withoutValueCount, onClose, onDone }) => {
	const createMutation = useCreateBlacklistEntries();
	const showSuccess = useErrorStore((s) => s.showSuccess);
	const showError = useErrorStore((s) => s.showError);
	const [reason, setReason] = useState('');
	const copy = COPY[entryType];

	const handleConfirm = async () => {
		try {
			const result =
				entries.length > 0
					? await createMutation.mutateAsync({ entries, reason: reason.trim() || undefined })
					: { added: 0, alreadyBlocked: 0, skipped: 0 };
			showSuccess({
				title: 'Blacklist aktualisiert',
				message: describeResult({ ...result, skipped: result.skipped + withoutValueCount }, copy),
			});
			onDone();
		} catch (error) {
			// The create mutation suppresses the global error toast so the form can word it itself.
			showError({ error, title: 'Blacklist konnte nicht aktualisiert werden' });
			throw error;
		}
	};

	return (
		<ConfirmModal
			isOpen
			onClose={onClose}
			onConfirm={handleConfirm}
			icon={BsSlashCircle}
			title={copy.title(selectedCount)}
			message={
				<div className="flex flex-col gap-3">
					<p>{copy.message}</p>
					<TextInput
						value={reason}
						onChange={(e) => setReason(e.target.value)}
						placeholder="Grund (optional)"
						maxLength={500}
					/>
				</div>
			}
			subtitle="Die Einträge bleiben in der Liste und lassen sich auf der Blacklist-Seite wieder entfernen."
			confirmText="Auf Blacklist setzen"
			isLoading={createMutation.isPending}
		/>
	);
};

BlacklistSelectionModal.propTypes = {
	entryType: PropTypes.oneOf(['email', 'domain']).isRequired,
	entries: PropTypes.arrayOf(
		PropTypes.shape({
			entryType: PropTypes.oneOf(['email', 'domain']).isRequired,
			value: PropTypes.string.isRequired,
		}),
	).isRequired,
	selectedCount: PropTypes.number.isRequired,
	// Selected rows that yielded no entry; reported as skipped next to the values the backend skips.
	withoutValueCount: PropTypes.number.isRequired,
	onClose: PropTypes.func.isRequired,
	onDone: PropTypes.func.isRequired,
};

export default BlacklistSelectionModal;
